import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Package, Clock, CheckCircle, XCircle, ShoppingCart } from "lucide-react";
import BottomNavigation from "@/components/BottomNavigation";
import { useCart } from "@/contexts/CartContext";

interface SwapRequest {
  id: string;
  current_device_brand: string;
  current_device_model: string;
  storage_capacity: string;
  battery_health: string;
  device_condition: string;
  status: string;
  vendor_offer_amount: number | null;
  vendor_offer_notes: string | null;
  created_at: string;
  front_image_url: string;
  back_image_url: string;
  additional_images: string[];
  target_menu_item: {
    id: string;
    name: string;
    price: number;
  };
  restaurant: {
    id: string;
    name: string;
  };
}

const SwapRequestsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [requests, setRequests] = useState<SwapRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchSwapRequests();
    }
  }, [user]);

  const fetchSwapRequests = async () => {
    try {
      const { data, error } = await supabase
        .from("swap_requests")
        .select(`
          *,
          target_menu_item:menu_items!swap_requests_target_menu_item_id_fkey(id, name, price),
          restaurant:restaurants!swap_requests_restaurant_id_fkey(id, name)
        `)
        .eq("customer_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error("Error fetching swap requests:", error);
      toast({
        title: "Error",
        description: "Failed to load swap requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOffer = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      const { error } = await supabase
        .from("swap_requests")
        .update({ 
          status: "accepted",
          responded_at: new Date().toISOString()
        })
        .eq("id", requestId);

      if (error) throw error;

      toast({
        title: "Offer accepted",
        description: "You can now proceed to checkout",
      });

      await fetchSwapRequests();
    } catch (error) {
      console.error("Error accepting offer:", error);
      toast({
        title: "Error",
        description: "Failed to accept offer",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeclineOffer = async (requestId: string) => {
    setProcessingId(requestId);
    try {
      const { error } = await supabase
        .from("swap_requests")
        .update({ 
          status: "declined",
          responded_at: new Date().toISOString()
        })
        .eq("id", requestId);

      if (error) throw error;

      toast({
        title: "Offer declined",
        description: "The vendor has been notified",
      });

      await fetchSwapRequests();
    } catch (error) {
      console.error("Error declining offer:", error);
      toast({
        title: "Error",
        description: "Failed to decline offer",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleProceedToCheckout = (request: SwapRequest) => {
    if (!request.vendor_offer_amount) return;

    // Add the swap item to cart with the vendor's offer price
    addItem({
      menu_item_id: request.target_menu_item.id,
      name: request.target_menu_item.name,
      price: request.vendor_offer_amount,
      quantity: 1,
      restaurant_id: request.restaurant.id,
      restaurant_name: request.restaurant.name,
      image_url: undefined,
      customizations: [],
      cart_type: 'marketplace'
    });


    navigate('/checkout');
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "Pending Review", className: "bg-yellow-100 text-yellow-800" },
      offer_made: { label: "Offer Made", className: "bg-blue-100 text-blue-800" },
      accepted: { label: "Accepted", className: "bg-green-100 text-green-800" },
      declined: { label: "Declined", className: "bg-red-100 text-red-800" },
      completed: { label: "Completed", className: "bg-gray-100 text-gray-800" },
      cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-800" },
    };

    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b border-gray-200 px-4 py-6 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Swap Requests</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No swap requests yet</h3>
              <p className="text-gray-600 mb-4">
                Visit a technology vendor and click the "Swap" button to trade in your device
              </p>
              <Button onClick={() => navigate("/marketplace/technology")}>
                Browse Tech Stores
              </Button>
            </CardContent>
          </Card>
        ) : (
          requests.map((request) => (
            <Card key={request.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{request.restaurant.name}</CardTitle>
                    <p className="text-sm text-gray-600">
                      {new Date(request.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Current Device */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Your Device</h4>
                  <div className="bg-gray-50 p-3 rounded-lg space-y-1 text-sm">
                    <p><span className="font-medium">Device:</span> {request.current_device_brand} {request.current_device_model}</p>
                    <p><span className="font-medium">Storage:</span> {request.storage_capacity}</p>
                    <p><span className="font-medium">Battery:</span> {request.battery_health}</p>
                    <p><span className="font-medium">Condition:</span> {request.device_condition}</p>
                  </div>
                </div>

                {/* Target Device */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Want to Swap To</h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    <p className="font-medium">{request.target_menu_item.name}</p>
                    <p className="text-gray-600">D{request.target_menu_item.price}</p>
                  </div>
                </div>

                {/* Images Preview */}
                <div>
                  <h4 className="font-semibold text-sm mb-2">Device Photos</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <img
                      src={request.front_image_url}
                      alt="Front"
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <img
                      src={request.back_image_url}
                      alt="Back"
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    {request.additional_images.slice(0, 1).map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Additional ${idx + 1}`}
                        className="w-full aspect-square object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>

                {/* Vendor Offer */}
                {request.status === "offer_made" && request.vendor_offer_amount && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold">Vendor Offer</h4>
                    </div>
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      D{request.vendor_offer_amount}
                    </p>
                    {request.vendor_offer_notes && (
                      <p className="text-sm text-gray-700">{request.vendor_offer_notes}</p>
                    )}
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        onClick={() => handleAcceptOffer(request.id)}
                        disabled={processingId === request.id}
                        className="flex-1"
                      >
                        {processingId === request.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Accept Offer"
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleDeclineOffer(request.id)}
                        disabled={processingId === request.id}
                        className="flex-1"
                      >
                        Decline
                      </Button>
                    </div>
                  </div>
                )}

                {/* Accepted - Proceed to Checkout */}
                {request.status === "accepted" && (
                  <Button
                    onClick={() => handleProceedToCheckout(request)}
                    className="w-full"
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Proceed to Checkout
                  </Button>
                )}

                {/* Status Messages */}
                {request.status === "pending" && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg">
                    <Clock className="h-4 w-4" />
                    <span>Waiting for vendor to review and make an offer</span>
                  </div>
                )}

                {request.status === "declined" && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <XCircle className="h-4 w-4" />
                    <span>You declined this offer</span>
                  </div>
                )}

                {request.status === "completed" && (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="h-4 w-4" />
                    <span>Swap completed successfully</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default SwapRequestsPage;